import { PrinterModel } from "../interfaces";
import { printersFetch } from "../actions";

import {
    Box,
    CircularProgress,
    Divider,
    Icon,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    OutlinedInput,
    Typography,
} from "@mui/material";
import useRequest from "api/useRequest";
import usePrevious from "hooks/usePrevious";
import { ChangeEvent, FC, useEffect, useState } from "react";
import {
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    RequestMessage,
} from "ui";

export interface IPrintPrinterSelectModal {
    title: string;
    search: string;
    handleChangeSearch: (value: string) => void;
    onSelectPrinter: (printer: PrinterModel) => void;
    onClose: () => void;
}

let timeout: ReturnType<typeof setTimeout>;

const PrintPrinterSelectModal: FC<IPrintPrinterSelectModal> = ({
    title,
    search,
    handleChangeSearch,
    onSelectPrinter,
    onClose,
}) => {
    const [searchVal, setSearchVal] = useState<string>(search);
    const prevValue = usePrevious(searchVal);
    const [data, setData] = useState<PrinterModel[]>([]);

    const { currentData, status, isError, isLoading, request, cancel } =
        useRequest();

    useEffect(() => {
        if (!isLoading && status === 200) {
            setData(currentData.data);
        }
    }, [currentData?.data, isLoading, status]);

    useEffect(() => {
        if (prevValue === undefined || prevValue === searchVal) {
            return;
        }

        clearTimeout(timeout);
        cancel();

        if (searchVal.length > 0) {
            timeout = setTimeout(() => {
                fetchPrinters(searchVal);
            }, 500);
        } else {
            setData([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cancel, searchVal, prevValue]);

    const fetchPrinters = (val: string) => {
        request(
            printersFetch({
                params: {
                    _search: val,
                    _sort: { serial_number: "asc" },
                },
            }),
        );
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchVal(e.target.value);
        handleChangeSearch(e.target.value);
    };

    const handleClearInput = () => {
        setSearchVal("");
        handleChangeSearch("");
    };

    const renderEndAdornment = () => {
        if (isLoading) {
            return (
                <InputAdornment position="end">
                    <CircularProgress size={20} />
                </InputAdornment>
            );
        }

        if (searchVal.length > 0) {
            return (
                <InputAdornment position="end">
                    <IconButton
                        onClick={handleClearInput}
                        sx={{ position: "absolute", right: 0 }}
                    >
                        <Icon>close</Icon>
                    </IconButton>
                </InputAdornment>
            );
        }
    };

    const renderContent = () => {
        if (isLoading) return null;

        if (isError) {
            return status === 404 ? (
                <Typography variant="body1" sx={{ textAlign: "center", mb: 2 }}>
                    No printers found
                </Typography>
            ) : (
                <RequestMessage status={status} />
            );
        }

        return (
            <List component="nav" sx={{ pt: 0 }}>
                {data.map((printer) => (
                    <Box key={printer.id}>
                        <ListItem button onClick={() => onSelectPrinter(printer)}>
                            <Box sx={{ flexGrow: 1 }}>
                                <ListItemText primary={printer.serial_number} />
                                <ListItemText
                                    secondary={printer.model_name}
                                    secondaryTypographyProps={{ fontSize: 12 }}
                                />
                                <ListItemText
                                    secondary={printer.status}
                                    secondaryTypographyProps={{ fontSize: 12 }}
                                />
                            </Box>
                        </ListItem>
                        <Divider />
                    </Box>
                ))}
            </List>
        );
    };

    return (
        <Modal
            open
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { position: "fixed", top: 0 } }}
        >
            <ModalTitle onClose={onClose}>{title}</ModalTitle>
            <ModalActions>
                <OutlinedInput
                    autoFocus
                    fullWidth
                    onChange={handleSearch}
                    placeholder={title}
                    size="small"
                    endAdornment={renderEndAdornment()}
                    startAdornment={
                        <InputAdornment position="start">
                            <Icon>search</Icon>
                        </InputAdornment>
                    }
                    value={searchVal}
                />
            </ModalActions>
            <ModalContent sx={{ pt: 0, px: 2 }}>{renderContent()}</ModalContent>
        </Modal>
    );
};

export default PrintPrinterSelectModal;
